<template>
  <div class="change-history">
    <p v-if="loading">Caricamento storico...</p>
    <p v-else-if="error" class="p-error">{{ error }}</p>
    <p v-else-if="!entries.length">Nessun cambio registrato.</p>
    <p v-else class="text-sm text-color-secondary">Gli ID sono quelli registrati al momento del cambio; i dettagli anagrafici mostrano i valori attuali.</p>
    <article v-for="entry in visibleEntries" :key="entry.id" class="change-history__event">
      <div class="change-history__heading">
        <strong>{{ kind === 'rappresentante' ? 'Cambio rappresentante' : 'Cambio contatto' }} del {{ formatDate(entry.data) }}</strong>
        <span>Operatore: {{ entry.operatore || `ID ${entry.user_id}` }}</span>
      </div>
      <div class="change-history__transition">
        <div class="change-history__card change-history__card--before">
          <span class="change-history__side-label">Prima</span>
          <div v-if="!entry.precedente_id">Nessuna associazione</div>
          <template v-else>
            <template v-if="kind === 'rappresentante' && entry.precedente">
              <router-link class="change-history__link" :to="representativePath(entry.precedente_id)" target="_blank">{{ entry.precedente.name || 'Senza nome' }}</router-link>
              <div>ID {{ entry.precedente_id }}<span v-if="entry.precedente.codfis"> · CF/P. IVA {{ entry.precedente.codfis }}</span></div>
            </template>
            <strong v-else>{{ label(entry.precedente, entry.precedente_id) }}</strong>
            <div v-if="kind === 'contatto' && entry.precedente" class="change-history__details">
              <div>
                Rappresentante:
                <router-link v-if="entry.precedente.rappresentante" class="change-history__link" :to="representativePath(entry.precedente.cod_rapp)" target="_blank">{{ entry.precedente.rappresentante.name || 'Senza nome' }}</router-link>
                <span v-else>{{ representativeLabel(entry.precedente) }}</span>
                <span v-if="entry.precedente.rappresentante"> · ID {{ entry.precedente.cod_rapp }}<span v-if="entry.precedente.rappresentante.codfis"> · CF/P. IVA {{ entry.precedente.rappresentante.codfis }}</span></span>
              </div>
              <div>{{ contactDetails(entry.precedente) }}</div>
            </div>
          </template>
        </div>
        <span class="change-history__arrow" aria-label="Passaggio da prima a dopo"><i class="pi pi-arrow-right" aria-hidden="true" /></span>
        <div class="change-history__card change-history__card--after">
          <span class="change-history__side-label">Dopo</span>
          <div v-if="!entry.nuovo_id">Nessuna associazione</div>
          <template v-else>
            <template v-if="kind === 'rappresentante' && entry.nuovo">
              <router-link class="change-history__link" :to="representativePath(entry.nuovo_id)" target="_blank">{{ entry.nuovo.name || 'Senza nome' }}</router-link>
              <div>ID {{ entry.nuovo_id }}<span v-if="entry.nuovo.codfis"> · CF/P. IVA {{ entry.nuovo.codfis }}</span></div>
            </template>
            <strong v-else>{{ label(entry.nuovo, entry.nuovo_id) }}</strong>
            <div v-if="kind === 'contatto' && entry.nuovo" class="change-history__details">
              <div>
                Rappresentante:
                <router-link v-if="entry.nuovo.rappresentante" class="change-history__link" :to="representativePath(entry.nuovo.cod_rapp)" target="_blank">{{ entry.nuovo.rappresentante.name || 'Senza nome' }}</router-link>
                <span v-else>{{ representativeLabel(entry.nuovo) }}</span>
                <span v-if="entry.nuovo.rappresentante"> · ID {{ entry.nuovo.cod_rapp }}<span v-if="entry.nuovo.rappresentante.codfis"> · CF/P. IVA {{ entry.nuovo.rappresentante.codfis }}</span></span>
              </div>
              <div>{{ contactDetails(entry.nuovo) }}</div>
            </div>
          </template>
        </div>
      </div>
    </article>
    <div v-if="!loading && !error && entries.length > pageSize" class="change-history__pagination">
      <span>{{ first + 1 }}–{{ pageEnd }} di {{ entries.length }} cambi</span>
      <prime-paginator :first="first" :rows="pageSize" :totalRecords="entries.length" paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink" @page="onPage" />
    </div>
  </div>
</template>

<script>
import PrimePaginator from 'primevue/paginator'

export default {
  name: 'StoricoCambioAnagrafica',
  components: { PrimePaginator },
  props: {
    kind: { type: String, required: true },
    entries: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    error: { type: String, default: '' }
  },
  data() {
    return { first: 0, pageSize: 5 }
  },
  computed: {
    visibleEntries() {
      return this.entries.slice(this.first, this.first + this.pageSize)
    },
    pageEnd() {
      return Math.min(this.first + this.pageSize, this.entries.length)
    }
  },
  watch: {
    entries() { this.first = 0 }
  },
  methods: {
    onPage(event) {
      this.first = event.first
    },
    representativePath(id) {
      return `/ask/rappresentanti/${encodeURIComponent(id)}/dettagli`
    },
    formatDate(value) {
      if (!value) return 'data non disponibile'
      const date = new Date(String(value).replace(' ', 'T'))
      return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('it-IT', { dateStyle: 'short', timeStyle: 'short' }).format(date)
    },
    label(entity, id) {
      if (!entity) return `ID ${id} (anagrafica non più disponibile)`
      if (this.kind === 'rappresentante') return `${entity.name || 'Senza nome'} · ID ${id}${entity.codfis ? ` · CF/P. IVA ${entity.codfis}` : ''}`
      return `Contatto ID ${id}`
    },
    representativeLabel(contact) {
      if (contact.cod_rapp === null || contact.cod_rapp === undefined) return 'Dato non disponibile'
      if (Number(contact.cod_rapp) === 0) return 'non associato'
      const representative = contact.rappresentante
      if (!representative) return `ID ${contact.cod_rapp} (anagrafica non più disponibile)`
      return representative.name || 'Senza nome'
    },
    contactDetails(contact) {
      const address = [contact.toponimo, contact.indirizzo, contact.civico, contact.cap].filter(Boolean).join(' ')
      return [address, contact.email && `Email: ${contact.email}`, contact.pec && `PEC: ${contact.pec}`, contact.cell && `Cellulare: ${contact.cell}`, contact.tel && `Telefono: ${contact.tel}`].filter(Boolean).join(' · ') || 'Nessun dettaglio disponibile'
    }
  }
}
</script>

<style scoped>
.change-history__event { padding: 1rem 0; border-bottom: 1px solid var(--surface-border); }
.change-history__event:last-child { border-bottom: 0; }
.change-history__heading { display: flex; flex-wrap: wrap; gap: 0.4rem 1rem; justify-content: space-between; align-items: baseline; margin-bottom: 0.75rem; }
.change-history__transition { display: grid; grid-template-columns: minmax(0, 1fr) 2.75rem minmax(0, 1fr); gap: 0.45rem; align-items: stretch; }
.change-history__card { padding: 0.85rem 1rem; border: 1px solid var(--surface-border); border-radius: 8px; overflow-wrap: anywhere; min-width: 0; }
.change-history__card--before { background: var(--surface-50); }
.change-history__card--after { background: var(--green-50, #f0fdf4); border-color: var(--green-200, #bbf7d0); }
.change-history__side-label { display: block; margin-bottom: 0.45rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--text-color-secondary); }
.change-history__details { margin-top: 0.4rem; line-height: 1.5; }
.change-history__link { color: var(--primary-color); font-weight: 700; text-decoration: underline; text-underline-offset: 2px; }
.change-history__link:hover { text-decoration-thickness: 2px; }
.change-history__arrow { display: flex; align-items: center; justify-content: center; align-self: center; justify-self: center; width: 2rem; height: 2rem; border-radius: 50%; background: var(--primary-color); color: var(--primary-color-text); }
.change-history__pagination { display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 0.5rem; padding-top: 0.75rem; }
.change-history__pagination :deep(.p-paginator) { border: 0; padding: 0; }
@media (max-width: 640px) {
  .change-history__transition { grid-template-columns: 1fr; gap: 0.5rem; }
  .change-history__arrow { transform: rotate(90deg); }
}
</style>
